/**
 * Drill Register Mapper
 * Transforms drill requirements into register rows with equipment dependencies
 * Following Domain Spine v2 architecture
 */

import { getDrillRequirements, DRILL_TYPES, COMPLIANCE_LEVELS } from '../../config/vesselRequirements/domains/drills/index.js';

/**
 * Equipment dependencies for each drill type
 * Maps drill types to the equipment categories and specific items they depend on
 */
const DRILL_EQUIPMENT_DEPENDENCIES = {
  fire: {
    categories: ['ffe'],
    specificItems: [
      'ffe_extinguisher_*', // All portable extinguishers
      'ffe_blanket',
      'ffe_fixedsystem_*', // All fixed systems
      'ffe_pump',
      'ffe_hose',
      'ffe_detector_*' // All detectors
    ],
    description: 'Fire fighting equipment'
  },
  
  abandon_ship: {
    categories: ['lsa'],
    specificItems: [
      'lsa_liferaft_*',
      'lsa_lifejacket_*',
      'lsa_immersionsuit_*',
      'lsa_lifebuoy_*',
      'lsa_epirb',
      'lsa_sart',
      'lsa_flares_*'
    ],
    description: 'Life saving appliances'
  },
  
  mob: {
    categories: ['lsa'],
    specificItems: [
      'lsa_lifebuoy_*',
      'lsa_danbuoy',
      'lsa_jonbuoy',
      'lsa_recovery_equipment',
      'lsa_mob_boat',
      'lsa_throwline'
    ],
    description: 'MOB recovery equipment'
  },
  
  pollution_response: {
    categories: ['pollution'],
    specificItems: [
      'pollution_kit',
      'pollution_absorbents',
      'pollution_booms',
      'pollution_dispersants',
      'sopep_equipment'
    ],
    description: 'Pollution response equipment'
  },
  
  gmdss_dsc: {
    categories: ['gmdss', 'nav'],
    specificItems: [
      'gmdss_vhf_dsc',
      'gmdss_mf_hf',
      'gmdss_inmarsat',
      'gmdss_navtex',
      'gmdss_epirb',
      'gmdss_sart'
    ],
    description: 'GMDSS communication equipment'
  },
  
  medical_emergency: {
    categories: ['medical'],
    specificItems: [
      'medical_cat_a',
      'medical_cat_b',
      'medical_cat_c',
      'medical_oxygen',
      'medical_aed',
      'medical_stretcher'
    ],
    description: 'Medical equipment and stores'
  },
  
  flooding: {
    categories: ['damage_control'],
    specificItems: [
      'damage_control_plugs',
      'damage_control_patches',
      'portable_pump',
      'submersible_pump',
      'emergency_pump',
      'watertight_doors'
    ],
    description: 'Damage control equipment'
  },
  
  steering_nav_fail: {
    categories: ['nav', 'machinery'],
    specificItems: [
      'emergency_steering',
      'emergency_tiller',
      'nav_backup_compass',
      'nav_backup_gps',
      'manual_steering_gear'
    ],
    description: 'Emergency steering and navigation'
  },
  
  electrical_blackout: {
    categories: ['electrical'],
    specificItems: [
      'emergency_generator',
      'emergency_batteries',
      'emergency_lighting',
      'ups_systems',
      'manual_controls'
    ],
    description: 'Emergency electrical systems'
  },
  
  enclosed_space_entry: {
    categories: ['safety'],
    specificItems: [
      'gas_detector_multi',
      'breathing_apparatus',
      'safety_harness',
      'rescue_tripod',
      'ventilation_equipment',
      'emergency_escape_set'
    ],
    description: 'Enclosed space entry equipment'
  },
  
  towing_emergency: {
    categories: ['deck'],
    specificItems: [
      'towing_gear',
      'emergency_towing_arrangement',
      'tow_quick_release',
      'chafing_gear',
      'towing_bridle'
    ],
    description: 'Towing equipment'
  },
  
  collision: {
    categories: ['damage_control', 'nav'],
    specificItems: [
      'collision_mat',
      'damage_control_kit',
      'watertight_doors',
      'emergency_pump',
      'nav_lights_emergency'
    ],
    description: 'Collision response equipment'
  },
  
  grounding: {
    categories: ['nav', 'machinery'],
    specificItems: [
      'kedge_anchor',
      'sounding_equipment',
      'portable_pump',
      'damage_assessment_tools',
      'emergency_propulsion'
    ],
    description: 'Grounding response equipment'
  }
};

/**
 * Transform a single drill requirement into a register row
 * @param {Object} requirement - Drill requirement object
 * @param {string} drillType - Type of drill
 * @param {string} vesselId - Vessel ID
 * @param {Object} drillRecords - Existing drill records for last completed/next due
 * @returns {Object} Register row
 */
function transformDrillToRegisterRow(requirement, drillType, vesselId, drillRecords = {}) {
  const drillRecord = drillRecords[drillType] || {};
  const dependencies = DRILL_EQUIPMENT_DEPENDENCIES[drillType] || {};
  
  // Generate stable ID
  const id = `drill:${vesselId}:${drillType}`;
  
  // Determine name - use custom name if available
  const name = requirement.customName || 
    drillType.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ') + ' Drill';
  
  return {
    id,
    drillType,
    name,
    frequencyDays: requirement.frequency_days,
    complianceLevel: requirement.compliance_level,
    regulatorySource: requirement.regulatory_source || 'Company SMS',
    note: requirement.note || '',
    linkedEquipmentIds: dependencies.specificItems || [],
    linkedEquipmentCategories: dependencies.categories || [],
    equipmentDescription: dependencies.description || '',
    lastCompleted: drillRecord.lastCompleted || null,
    nextDue: drillRecord.nextDue || null,
    configurable: requirement.configurable !== false,
    custom: requirement.custom === true,
    warning: requirement.warning || null,
    riskDependent: requirement.risk_dependent === true
  };
}

/**
 * Map drill requirements to register rows
 * @param {Object} vesselConfig - Vessel configuration
 * @param {string} vesselId - Vessel ID
 * @param {Object} drillRecords - Existing drill records
 * @param {Object} equipmentRegister - Equipment register for cross-reference
 * @returns {Array} Array of drill register rows
 */
export function mapDrillsToRegister(vesselConfig, vesselId, drillRecords = {}, equipmentRegister = null) {

  // Get drill requirements for this vessel
  const drillRequirements = getDrillRequirements(vesselConfig, equipmentRegister);

  // Transform each requirement into a register row
  const registerRows = [];
  
  Object.entries(drillRequirements).forEach(([drillType, requirement]) => {
    // Skip info entries
    if (drillType.startsWith('_')) {
      return;
    }
    
    const row = transformDrillToRegisterRow(
      requirement,
      drillType,
      vesselId,
      drillRecords
    );
    
    registerRows.push(row);
  });

  return registerRows;
}

/**
 * Get equipment dependencies for a specific drill
 * @param {string} drillType - Type of drill
 * @param {Object} equipmentRegister - Equipment register
 * @returns {Array} Array of equipment IDs that this drill depends on
 */
export function getDrillEquipmentDependencies(drillType, equipmentRegister) {
  const dependencies = DRILL_EQUIPMENT_DEPENDENCIES[drillType];
  if (!dependencies) return [];
  
  const linkedEquipment = [];
  
  // If we have an equipment register, find actual equipment items
  if (equipmentRegister && equipmentRegister.items) {
    equipmentRegister.items.forEach(item => {
      // Check if item category matches
      if (dependencies.categories?.includes(item.category)) {
        linkedEquipment.push(item.id);
      }

      // Check if item ID matches specific patterns
      dependencies.specificItems?.forEach(pattern => {
        const regex = new RegExp('^' + pattern.replace('*', '.*') + '$');
        if (regex.test(item.id)) {
          if (!linkedEquipment.includes(item.id)) {
            linkedEquipment.push(item.id);
          }
        }
      });
    });
  }
  
  return linkedEquipment;
}

/**
 * Check if drill has all required equipment
 * @param {string} drillType - Type of drill
 * @param {Object} equipmentRegister - Equipment register
 * @returns {Object} Status object with isReady and missingItems
 */
export function checkDrillEquipmentReadiness(drillType, equipmentRegister) {
  const dependencies = DRILL_EQUIPMENT_DEPENDENCIES[drillType];
  if (!dependencies) {
    return { isReady: true, missingItems: [] };
  }
  
  const requiredItems = dependencies.specificItems || [];
  const presentItems = getDrillEquipmentDependencies(drillType, equipmentRegister);
  
  // For now, simple check - drill is ready if it has at least some equipment
  // In future, could have more specific requirements per drill type
  const isReady = presentItems.length > 0;
  
  return {
    isReady,
    presentItems,
    requiredCategories: dependencies.categories,
    description: dependencies.description
  };
}

/**
 * Get all drills that depend on a specific equipment item
 * @param {string} equipmentId - Equipment item ID
 * @returns {Array} Array of drill types that depend on this equipment
 */
export function getDrillsForEquipment(equipmentId) {
  const dependentDrills = [];
  
  Object.entries(DRILL_EQUIPMENT_DEPENDENCIES).forEach(([drillType, deps]) => {
    // Check if equipment ID matches any pattern
    const matches = deps.specificItems.some(pattern => {
      const regex = new RegExp('^' + pattern.replace('*', '.*') + '$');
      return regex.test(equipmentId);
    });
    
    if (matches) {
      dependentDrills.push(drillType);
    }
  });
  
  return dependentDrills;
}

/**
 * Get summary statistics for drill register
 * @param {Array} drillRegister - Array of drill register rows
 * @returns {Object} Summary statistics
 */
export function getDrillRegisterStats(drillRegister) {
  const stats = {
    total: drillRegister.length,
    required: 0,
    recommended: 0,
    optional: 0,
    custom: 0,
    overdue: 0,
    dueSoon: 0,
    compliant: 0
  };
  
  const now = new Date();
  const soon = new Date();
  soon.setDate(soon.getDate() + 7); // Due within 7 days
  
  drillRegister.forEach(drill => {
    // Compliance level counts
    switch (drill.complianceLevel) {
      case COMPLIANCE_LEVELS.REQUIRED:
        stats.required++;
        break;
      case COMPLIANCE_LEVELS.RECOMMENDED:
        stats.recommended++;
        break;
      case COMPLIANCE_LEVELS.OPTIONAL:
        stats.optional++;
        break;
    }
    
    // Custom drill count
    if (drill.custom) {
      stats.custom++;
    }
    
    // Due status
    if (drill.nextDue) {
      const dueDate = new Date(drill.nextDue);
      if (dueDate < now) {
        stats.overdue++;
      } else if (dueDate < soon) {
        stats.dueSoon++;
      } else {
        stats.compliant++;
      }
    }
  });
  
  return stats;
}

// Export constants for external use
export { DRILL_TYPES, COMPLIANCE_LEVELS, DRILL_EQUIPMENT_DEPENDENCIES };

// Default export
export default {
  mapDrillsToRegister,
  getDrillEquipmentDependencies,
  checkDrillEquipmentReadiness,
  getDrillsForEquipment,
  getDrillRegisterStats
};